import {
  type SessionState,
  QueuedTypeName,
  ActiveTypeName,
  InactiveTypeName,
  ManualInterventionWorkflowStateStatus,
  ActiveWorkflowStateStatus,
  ActiveWorkflowState,
  type QueuedSessionState,
  type ActiveSessionState,
  type InactiveSessionState,
  type ManualInterventionWorkflowState,
} from "@financeable/brokerloop-data-plane";

// Type guard for checking queued session state.
const isQueuedType = (value: unknown): value is QueuedTypeName => {
  return Object.values(QueuedTypeName).includes(value as QueuedTypeName);
};

export const isQueuedSessionState = (state: SessionState | undefined): state is QueuedSessionState => {
  return isQueuedType(state?.type);
};

// Type guard for checking active session state.
const isActiveType = (value: unknown): value is ActiveTypeName => {
  return Object.values(ActiveTypeName).includes(value as ActiveTypeName);
};

export const isActiveSessionState = (
  state: SessionState | Omit<ActiveSessionState, "expiresAt"> | undefined,
): state is ActiveSessionState => {
  return isActiveType(state?.type);
};

// Type guard for checking inactive session state.
const isInactiveType = (value: unknown): value is InactiveTypeName => {
  return Object.values(InactiveTypeName).includes(value as InactiveTypeName);
};

export const isInactiveSessionState = (state: SessionState | undefined): state is InactiveSessionState => {
  return isInactiveType(state?.type);
};

// Type guard for checking active workflow state within an active session state.
const isActiveWorkflowStateStatus = (value: unknown): value is ActiveWorkflowStateStatus => {
  return Object.values(ActiveWorkflowStateStatus).includes(value as ActiveWorkflowStateStatus);
};

export const isActiveWorkflowState = (
  workflowState: ActiveSessionState["workflowState"] | undefined,
): workflowState is ActiveWorkflowState => {
  return isActiveWorkflowStateStatus(workflowState?.status);
};

// Type guard for checking manual intervention workflow state within an active session state.
const isManualInterventionWorkflowStateStatus = (value: unknown): value is ManualInterventionWorkflowStateStatus => {
  return Object.values(ManualInterventionWorkflowStateStatus).includes(value as ManualInterventionWorkflowStateStatus);
};

export const isManualInterventionWorkflowState = (
  workflowState: ActiveSessionState["workflowState"] | undefined,
): workflowState is ManualInterventionWorkflowState => {
  return isManualInterventionWorkflowStateStatus(workflowState?.status);
};
